import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { usePortfolio } from '@/lib/portfolio';
import { CustomSelect } from '@/components/CustomSelect';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, ScatterChart, Scatter, Cell, ZAxis, PieChart, Pie } from 'recharts';
import { Building2, TrendingUp, Info, ChevronRight, Plus, Search, Square, CheckSquare, MinusSquare, SlidersHorizontal, X, Brain, Zap, AlertTriangle, Shield, ArrowUpRight, ArrowDownRight } from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const fmtKpi = (v) => { const abs = Math.abs(v); return abs >= 1e6 ? `€${(v/1e6).toFixed(1)}M` : abs >= 1e3 ? `€${(v/1e3).toFixed(0)}k` : fmt(v); };
const chartTip = { background: '#fff', border: '1px solid #E2DDD3', borderRadius: 8, fontSize: 12, fontFamily: 'Inter, system-ui, sans-serif', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', padding: '8px 12px' };
const sColor = (v, good, warn) => v >= good ? '#2E6F57' : v >= warn ? '#A67C52' : '#8A3C3C';
const COLORS = ['#1F3A34', '#9C9488', '#D9D3C8', '#285047', '#A67C52'];

function mp(p, r, y) { if (r === 0) return y > 0 ? p / (y * 12) : 0; const rm = r / 12, n = y * 12; return p * (rm * Math.pow(1 + rm, n)) / (Math.pow(1 + rm, n) - 1); }

function calc(a, l, stressFactor = 1) {
  const mv = (a.market_value || 0), ar = (a.annual_rent || 0), vr = a.vacancy_rate || 0, oc = a.operating_costs || 0;
  const effRent = ar * stressFactor * (1 - vr / 100), noi = effRent - oc, la = l?.amount || 0;
  const ir = (l?.interest_rate || 0), ads = mp(la, ir / 100 * (stressFactor < 1 ? 1 : (1 + (stressFactor - 1) * 0.3)), l?.term_years || 30) * 12;
  return { mv, noi, la, ads, eq: mv - la, ltv: mv > 0 ? la / mv * 100 : 0, dscr: ads > 0 ? noi / ads : 0, ncf: noi - ads, ny: mv > 0 ? noi / mv * 100 : 0 };
}

function genHist(pairs, mo = 60) {
  const now = new Date(), h = [];
  for (let m = mo; m >= 0; m--) {
    const d = new Date(now); d.setDate(d.getDate() - m * 30);
    const f = Math.pow(1.04, (mo / 12 - m / 12)) / Math.pow(1.04, mo / 12);
    let dip = 1; if (m >= 26 && m <= 36) dip = 0.94 + 0.06 * Math.abs(m - 31) / 5;
    const factor = f * dip * (1 + 0.005 * Math.sin(m * Math.PI / 6));
    let tv = 0, td = 0, tCf = 0;
    for (const { a, l } of pairs) {
      const mv = (a.market_value || 0) * factor, la = l?.amount || 0;
      const ar = (a.annual_rent || 0) * (0.92 + 0.08 * factor);
      const noi = ar * (1 - (a.vacancy_rate || 0) / 100) - (a.operating_costs || 0);
      const ads = mp(la, (l?.interest_rate || 0) / 100, l?.term_years || 30) * 12;
      tv += mv; td += la; tCf += noi - ads;
    }
    h.push({ date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, value: Math.round(tv), equity: Math.round(tv - td), cashflow: Math.round(tCf / 12) });
  }
  return h;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { assets: ctxAssets, portfolios: allPortfolios, activeId: activePfId, activeName, loading: pfLoading, switchPortfolio } = usePortfolio();
  const [loading, setLoading] = useState(true);
  const [selIds, setSelIds] = useState(new Set());
  const [scenario, setScenario] = useState('base');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [execMode, setExecMode] = useState(false);

  const portfolio = { name: activeName, portfolio_id: activePfId, assets: ctxAssets };

  useEffect(() => {
    if (pfLoading) return;
    setSelIds(new Set(ctxAssets.map(a => a.asset_id)));
    setLoading(false);
  }, [pfLoading, ctxAssets]);

  const stressFactor = { base: 1, rent: 0.95, vacancy: 0.93, rate: 1, combined: 0.88 }[scenario] || 1;

  const intel = useMemo(() => {
    if (!portfolio?.assets?.length) return null;
    const sel = portfolio.assets.filter(a => selIds.has(a.asset_id));
    if (!sel.length) return null;

    let tv = 0, td = 0, tNoi = 0, tAds = 0, tCf = 0;
    const enriched = sel.map(a => {
      const m = calc(a, a.loan, stressFactor);
      tv += m.mv; td += m.la; tNoi += m.noi; tAds += m.ads; tCf += m.ncf;
      const ir = a.metrics?.irr || 0;
      const refiSpread = (a.loan?.interest_rate || 0) - 3.0;
      const refiPotential = refiSpread > 0.3 ? Math.round((a.loan?.amount || 0) * refiSpread / 100) : 0;
      const mktGap = (a.annual_rent || 0) > 0 ? 12 : 0;
      const revPotential = Math.round((a.annual_rent || 0) * mktGap / 100);
      const eqTrapped = Math.max(0, m.eq - m.mv * 0.25);
      const riskScore = Math.min(100, Math.max(0, Math.round(80 - m.dscr * 30 + ((a.loan?.interest_rate || 0) > 4 ? 15 : 0))));
      const quadrant = riskScore < 40 && ir >= 12 ? 'Core' : riskScore < 40 ? 'Optimize' : ir >= 12 ? 'Reduce Risk' : 'Divest';
      return { ...a, cm: m, ir, riskScore, refiPotential, revPotential, eqTrapped, quadrant, refiSpread };
    });

    const ltv = tv > 0 ? td / tv * 100 : 0;
    const dscr = tAds > 0 ? tNoi / tAds : 0;
    const ny = tv > 0 ? tNoi / tv * 100 : 0;
    const pirr = (() => { const tw = enriched.reduce((s, a) => s + Math.max(a.cm.eq, 1), 0); return enriched.reduce((s, a) => s + a.ir * Math.max(a.cm.eq, 1) / tw, 0); })();
    const covBuffer = ((dscr - 1.25) / 1.25 * 100);
    const liqMonths = tAds > 0 ? Math.round(435000 / (tAds / 12)) : 99;

    // Scores
    const stratScore = Math.round(Math.min(100, (dscr >= 1.25 ? 18 : 8) + (ltv <= 65 ? 16 : 6) + (pirr >= 12 ? 18 : 8) + (ny >= 4 ? 16 : 6) + 14 + 12));
    const riskScorePort = Math.round(enriched.reduce((s, a) => s + a.riskScore, 0) / enriched.length);
    const bankScore = Math.round(Math.min(100, (dscr >= 1.25 ? 25 : 10) + (ltv <= 65 ? 25 : 10) + (ny >= 4 ? 20 : 8) + (liqMonths >= 12 ? 15 : 5) + 15));
    const resilience = Math.round(Math.min(100, (70 - ltv) * 1.5 + (tCf > 0 ? 20 : 5) + 14 + 12 + Math.min(20, liqMonths)));

    // Segments + Cities
    const segs = {}; enriched.forEach(a => { const t = a.property_type || 'other'; segs[t] = (segs[t] || 0) + (a.annual_rent || 0); });
    const totalRent = enriched.reduce((s, a) => s + (a.annual_rent || 0), 0);
    const segData = Object.entries(segs).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, pct: totalRent > 0 ? Math.round(v / totalRent * 100) : 0 }));

    const scatter = enriched.map(a => ({ name: a.name, risk: a.riskScore, ret: a.ir, quadrant: a.quadrant }));

    const history = genHist(enriched.map(a => ({ a, l: a.loan })));

    // Alerts
    const alerts = [];
    enriched.filter(a => a.cm.dscr < 1.2).forEach(a => alerts.push({ sev: 'high', text: `${a.name}: DSCR ${a.cm.dscr.toFixed(2)} below covenant`, impact: 'DSCR breach risk' }));
    enriched.filter(a => a.refiSpread > 0.8).forEach(a => { if (alerts.length < 5) alerts.push({ sev: 'medium', text: `${a.name}: Rate ${(a.loan?.interest_rate||0).toFixed(1)}% — ${a.refiSpread.toFixed(1)}% above market`, impact: `Refi saves ${fmt(a.refiPotential)}/yr` }); });

    return {
      enriched, tv, td, tCf, ltv, dscr, ny, pirr, covBuffer, liqMonths,
      stratScore, riskScorePort, bankScore, resilience,
      segData, scatter, history, alerts, total: portfolio.assets.length,
    };
  }, [portfolio, selIds, stressFactor]);

  const tableAssets = useMemo(() => {
    if (!intel) return [];
    let list = [...intel.enriched];
    if (search) { const s = search.toLowerCase(); list = list.filter(a => a.name?.toLowerCase().includes(s) || a.city?.toLowerCase().includes(s)); }
    list.sort((a, b) => {
      let va, vb;
      if (sortBy === 'name') { va = a.name || ''; vb = b.name || ''; }
      else if (sortBy === 'irr') { va = a.ir; vb = b.ir; }
      else if (sortBy === 'risk') { va = a.riskScore; vb = b.riskScore; }
      else if (sortBy === 'dscr') { va = a.cm.dscr; vb = b.cm.dscr; }
      else if (sortBy === 'cashflow') { va = a.cm.ncf; vb = b.cm.ncf; }
      else if (sortBy === 'refi') { va = a.refiPotential; vb = b.refiPotential; }
      else { va = a.name; vb = b.name; }
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return list;
  }, [intel, search, sortBy, sortDir]);

  const toggleSort = (col) => { if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortBy(col); setSortDir(col === 'risk' || col === 'refi' || col === 'irr' ? 'desc' : 'asc'); } };
  const toggleAsset = (id) => setSelIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><p className="text-[14px] text-[#8C877F] animate-pulse">Loading...</p></div>;
  if (!portfolio?.assets?.length) return (
    <div className="flex flex-col items-center justify-center h-[60vh] text-center space-y-4"><Building2 className="w-10 h-10 text-[#1F3A34]/25" /><h2 className="font-serif text-[32px] text-[#1C1C1C]">Welcome, {user?.name?.split(' ')[0]}</h2><p className="text-[14px] text-[#8C877F] max-w-xs">Your portfolio is being set up.</p></div>
  );
  if (!intel) return null;

  return (
    <div className="space-y-7 animate-fade-in">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Portfolio Command Center</p>
          <div className="flex items-center gap-3">
            <h1 className="font-serif text-[36px] text-[#1C1C1C] font-medium tracking-tight leading-tight">{portfolio.name}</h1>

          </div>
        </div>
        <div className="flex items-center gap-2">
          {Object.entries({ base: 'Base', rent: 'Rent -5%', vacancy: 'Vacancy', rate: 'Rate +1%', combined: 'Stress' }).map(([id, l]) => (
            <button key={id} onClick={() => setScenario(id)} className={`px-2.5 py-1.5 rounded-lg text-[10px] font-sans font-medium transition-all ${scenario === id ? 'bg-[#1F3A34] text-white' : 'bg-[#F4F1EB] text-[#5A5A5A] hover:bg-[#ECE7DE]'}`}>{l}</button>
          ))}
          <button onClick={() => navigate('/add-asset')} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1F3A34] text-white rounded-lg text-[11px] font-sans font-medium hover:bg-[#285047]"><Plus className="w-3 h-3" /> Add</button>
        </div>
      </div>

      {/* Hero KPIs */}
      <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-11 gap-3">
        {[
          { l: 'Value', v: fmtKpi(intel.tv) },
          { l: 'Equity', v: fmtKpi(intel.tv - intel.td) },
          { l: 'CF/mo', v: fmtKpi(intel.tCf / 12), c: intel.tCf >= 0 ? '#2E6F57' : '#8A3C3C' },
          { l: 'LTV', v: `${intel.ltv.toFixed(1)}%`, c: sColor(100 - intel.ltv, 35, 30) },
          { l: 'IRR', v: `${intel.pirr.toFixed(1)}%`, c: sColor(intel.pirr, 10, 8) },
          { l: 'DSCR', v: intel.dscr.toFixed(2), c: sColor(intel.dscr, 1.25, 1.1) },
          { l: 'Strategy', v: `${intel.stratScore}`, c: sColor(intel.stratScore, 70, 50) },
          { l: 'Risk', v: `${intel.riskScorePort}`, c: sColor(100 - intel.riskScorePort, 60, 40) },
          { l: 'Bank', v: `${intel.bankScore}`, c: sColor(intel.bankScore, 70, 50) },
          { l: 'Resilience', v: `${intel.resilience}`, c: sColor(intel.resilience, 60, 40) },
          { l: 'Buffer', v: `${intel.covBuffer.toFixed(0)}%`, c: sColor(intel.covBuffer, 10, 0) },
        ].map(k => (
          <div key={k.l} className="bg-white border border-[#E2DDD3] rounded-xl p-3" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <p className="text-[9px] text-[#8C877F] font-sans font-medium">{k.l}</p>
            <p className="text-[15px] font-sans font-semibold leading-none truncate mt-1" style={{ color: k.c || '#1C1C1C' }}>{k.v}</p>
          </div>
        ))}
      </div>

      {/* AI Summary + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div className="flex items-center gap-2 mb-3"><Brain className="w-4 h-4 text-[#1F3A34]" /><p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">AI Executive Summary</p></div>
          <p className="text-[13px] text-[#1C1C1C] font-sans leading-relaxed">
            Portfolio {intel.pirr >= 12 ? 'exceeds' : 'approaches'} IRR target at {intel.pirr.toFixed(1)}% with {intel.dscr.toFixed(2)}x DSCR ({intel.covBuffer.toFixed(0)}% above covenant).
            {intel.ltv > 60 ? ` LTV at ${intel.ltv.toFixed(1)}% — monitor leverage position.` : ` LTV at ${intel.ltv.toFixed(1)}% within target.`}
            {' '}Liquidity runway {intel.liqMonths} months under {scenario === 'base' ? 'base case' : 'stress'}.
            {intel.enriched.filter(a => a.refiPotential > 0).length > 0 && ` ${intel.enriched.filter(a => a.refiPotential > 0).length} asset(s) with refinancing potential totaling ${fmt(intel.enriched.reduce((s, a) => s + a.refiPotential, 0))}/yr.`}
          </p>
        </div>
        {intel.alerts.length > 0 && (
          <div className="bg-white border border-[#A67C52]/20 rounded-xl p-5 space-y-2" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#A67C52]" /><p className="text-[12px] text-[#1C1C1C] font-sans font-semibold">Alerts ({intel.alerts.length})</p></div>
            {intel.alerts.slice(0, 3).map((a, i) => (
              <div key={i} className="flex items-start gap-2 py-1.5 border-b border-[#F4F1EB] last:border-0">
                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 ${a.sev === 'high' ? 'bg-[#8A3C3C]' : 'bg-[#A67C52]'}`} />
                <div><p className="text-[11px] text-[#1C1C1C] font-sans">{a.text}</p><p className="text-[10px] text-[#2E6F57] font-sans font-medium">{a.impact}</p></div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-4">Value & Equity — 5 Year</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={intel.history}>
              <defs><linearGradient id="gEq" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1F3A34" stopOpacity={0.1} /><stop offset="95%" stopColor="#1F3A34" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#E3DED6" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} interval={11} />
              <YAxis tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1e6).toFixed(1)}M`} width={42} />
              <RTooltip contentStyle={chartTip} formatter={v => [fmt(v), '']} />
              <Area type="monotone" dataKey="value" stroke="#9C9488" strokeWidth={1} fill="none" />
              <Area type="monotone" dataKey="equity" stroke="#1F3A34" strokeWidth={1.8} fill="url(#gEq)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-4">Risk vs Return</p>
          <ResponsiveContainer width="100%" height={220}>
            <ScatterChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <CartesianGrid stroke="#E3DED6" />
              <XAxis type="number" dataKey="risk" tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} domain={[0, 100]} />
              <YAxis type="number" dataKey="ret" tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} domain={[0, 25]} />
              <ZAxis range={[30, 120]} />
              <RTooltip contentStyle={chartTip} />
              <Scatter data={intel.scatter}>{intel.scatter.map((d, i) => <Cell key={i} fill={d.quadrant === 'Core' ? '#2E6F57' : d.quadrant === 'Optimize' ? '#1F3A34' : d.quadrant === 'Reduce Risk' ? '#A67C52' : '#8A3C3C'} />)}</Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <div className="flex gap-3 mt-2 justify-center">{[{ l: 'Core', c: '#2E6F57' }, { l: 'Optimize', c: '#1F3A34' }, { l: 'Risk', c: '#A67C52' }, { l: 'Divest', c: '#8A3C3C' }].map(q => <div key={q.l} className="flex items-center gap-1"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: q.c }} /><span className="text-[9px] text-[#8C877F]">{q.l}</span></div>)}</div>
        </div>
      </div>

      {/* Asset Table */}
      <div className="bg-white border border-[#E2DDD3] rounded-xl overflow-hidden" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <div className="px-5 py-3 border-b border-[#E2DDD3] flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-[240px]"><Search className="w-4 h-4 text-[#8C877F] absolute left-3 top-1/2 -translate-y-1/2" /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="w-full pl-9 pr-3 py-2 bg-[#F4F1EB] border-none rounded-lg text-[13px] font-sans placeholder:text-[#D9D3C8] focus:outline-none focus:ring-1 focus:ring-[#1F3A34]/20" /></div>

          <div className="h-5 w-px bg-[#E2DDD3]" />

          {/* Selection controls */}
          <button onClick={() => setSelIds(new Set(tableAssets.map(a => a.asset_id)))}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-sans font-medium text-[#5A5A5A] hover:bg-[#E6EFEA] hover:text-[#1F3A34] transition-colors">
            <CheckSquare className="w-3.5 h-3.5" /> Select All
          </button>
          <button onClick={() => setSelIds(new Set())}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-sans font-medium text-[#5A5A5A] hover:bg-[#FDF5F5] hover:text-[#8A3C3C] transition-colors">
            <Square className="w-3.5 h-3.5" /> Deselect All
          </button>
          <button onClick={() => { const inv = new Set(portfolio.assets.map(a => a.asset_id)); selIds.forEach(id => inv.delete(id)); portfolio.assets.filter(a => !selIds.has(a.asset_id)).forEach(a => inv.add(a.asset_id)); setSelIds(inv); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-sans font-medium text-[#5A5A5A] hover:bg-[#F4F1EB] transition-colors">
            <MinusSquare className="w-3.5 h-3.5" /> Invert
          </button>

          <div className="h-5 w-px bg-[#E2DDD3]" />

          {/* Quick filters */}
          <button onClick={() => { setSelIds(new Set(intel.enriched.filter(a => a.quadrant === 'Core').map(a => a.asset_id))); }}
            className="px-2.5 py-1.5 rounded-lg text-[10px] font-sans font-medium bg-[#E6EFEA] text-[#2E6F57] hover:bg-[#2E6F57] hover:text-white transition-colors">Core Only</button>
          <button onClick={() => { setSelIds(new Set(intel.enriched.filter(a => a.riskScore > 50).map(a => a.asset_id))); }}
            className="px-2.5 py-1.5 rounded-lg text-[10px] font-sans font-medium bg-[#FDF5F5] text-[#8A3C3C] hover:bg-[#8A3C3C] hover:text-white transition-colors">High Risk</button>
          <button onClick={() => { setSelIds(new Set(intel.enriched.filter(a => a.refiPotential > 0).map(a => a.asset_id))); }}
            className="px-2.5 py-1.5 rounded-lg text-[10px] font-sans font-medium bg-[#FDF8F0] text-[#A67C52] hover:bg-[#A67C52] hover:text-white transition-colors">Refi Candidates</button>

          <div className="ml-auto">
            <span className="text-[11px] font-sans font-medium" style={{ color: selIds.size === intel.total ? '#2E6F57' : '#1F3A34' }}>{selIds.size}/{intel.total} selected</span>
          </div>
        </div>
        <div className="grid grid-cols-10 gap-1 px-5 py-2.5 border-b border-[#E2DDD3] text-[9px] text-[#8C877F] font-sans font-medium uppercase tracking-wider">
          <span className="col-span-2">Asset</span>
          <button onClick={() => toggleSort('cashflow')} className="text-right hover:text-[#1C1C1C] cursor-pointer">CF/mo</button>
          <button onClick={() => toggleSort('dscr')} className="text-right hover:text-[#1C1C1C] cursor-pointer">DSCR {sortBy === 'dscr' && (sortDir === 'asc' ? '↑' : '↓')}</button>
          <button onClick={() => toggleSort('irr')} className="text-right hover:text-[#1C1C1C] cursor-pointer">IRR {sortBy === 'irr' && (sortDir === 'asc' ? '↑' : '↓')}</button>
          <button onClick={() => toggleSort('risk')} className="text-right hover:text-[#1C1C1C] cursor-pointer">Risk {sortBy === 'risk' && (sortDir === 'asc' ? '↑' : '↓')}</button>
          <button onClick={() => toggleSort('refi')} className="text-right hover:text-[#1C1C1C] cursor-pointer">Refi {sortBy === 'refi' && (sortDir === 'asc' ? '↑' : '↓')}</button>
          <span className="text-right">Yield</span>
          <span className="text-right">LTV</span>
          <span className="text-center">Class</span>
        </div>
        {tableAssets.map(a => (
          <div key={a.asset_id} className={`grid grid-cols-10 gap-1 px-5 py-3 border-b border-[#F4F1EB] last:border-0 text-[12px] transition-colors ${a.cm.dscr < 1.2 ? 'bg-[#FDF5F5]/30' : 'hover:bg-[#F4F1EB]/40'} ${!selIds.has(a.asset_id) ? 'opacity-40' : ''}`}>
            <div className="col-span-2 flex items-center gap-2">
              <button onClick={e => { e.stopPropagation(); toggleAsset(a.asset_id); }}>{selIds.has(a.asset_id) ? <CheckSquare className="w-4 h-4 text-[#1F3A34]" /> : <Square className="w-4 h-4 text-[#D9D3C8]" />}</button>
              <div className="cursor-pointer min-w-0" onClick={() => navigate(`/assets/${a.asset_id}`)}>
                <p className="text-[13px] text-[#1C1C1C] font-sans font-medium truncate">{a.name}</p>
                <p className="text-[10px] text-[#8C877F]">{a.city}</p>
              </div>
            </div>
            <p className={`text-right font-sans font-semibold self-center ${a.cm.ncf >= 0 ? 'text-[#2E6F57]' : 'text-[#8A3C3C]'}`}>{fmt(a.cm.ncf / 12)}</p>
            <p className="text-right font-sans font-semibold self-center" style={{ color: sColor(a.cm.dscr, 1.25, 1.1) }}>{a.cm.dscr.toFixed(2)}</p>
            <p className="text-right font-sans font-semibold text-[#1F3A34] self-center">{a.ir.toFixed(1)}%</p>
            <p className="text-right font-sans font-semibold self-center" style={{ color: sColor(100 - a.riskScore, 60, 40) }}>{a.riskScore}</p>
            <p className="text-right font-sans self-center" style={{ color: a.refiPotential > 0 ? '#2E6F57' : '#8C877F' }}>{a.refiPotential > 0 ? fmt(a.refiPotential) : '—'}</p>
            <p className="text-right font-sans font-semibold self-center">{a.cm.ny.toFixed(1)}%</p>
            <p className="text-right font-sans font-semibold self-center">{a.cm.ltv.toFixed(0)}%</p>
            <div className="flex justify-center self-center"><span className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: a.quadrant === 'Core' ? '#E6EFEA' : a.quadrant === 'Optimize' ? '#E6EFEA' : a.quadrant === 'Reduce Risk' ? '#FDF8F0' : '#FDF5F5', color: a.quadrant === 'Core' ? '#2E6F57' : a.quadrant === 'Optimize' ? '#1F3A34' : a.quadrant === 'Reduce Risk' ? '#A67C52' : '#8A3C3C' }}>{a.quadrant}</span></div>
          </div>
        ))}
        <div className="px-5 py-2.5 bg-[#F4F1EB]/50 text-[11px] text-[#8C877F] font-sans flex justify-between">
          <span>{tableAssets.length} assets</span><span>{scenario !== 'base' && <span className="text-[#8A3C3C]">Stress scenario active</span>}</span>
        </div>
      </div>

      {/* AI Optimization */}
      <div className="bg-[#E6EFEA] border border-[#1F3A34]/10 rounded-xl p-5 space-y-2">
        <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#1F3A34]" /><p className="text-[12px] text-[#1F3A34] font-sans font-semibold">AI Optimization Opportunities</p></div>
        {intel.enriched.filter(a => a.refiPotential > 0).sort((a, b) => b.refiPotential - a.refiPotential).slice(0, 2).map((a, i) => (
          <p key={i} className="text-[12px] text-[#1C1C1C] font-sans pl-5">Refinance <strong>{a.name}</strong> → <strong className="text-[#2E6F57]">+{fmt(a.refiPotential)}/yr</strong> cashflow, DSCR improves.</p>
        ))}
        {intel.enriched.filter(a => a.revPotential > 0).sort((a, b) => b.revPotential - a.revPotential).slice(0, 1).map((a, i) => (
          <p key={`r${i}`} className="text-[12px] text-[#1C1C1C] font-sans pl-5">Rent optimization on <strong>{a.name}</strong> → <strong className="text-[#2E6F57]">+{fmt(a.revPotential)}/yr</strong> uplift potential.</p>
        ))}
      </div>
    </div>
  );
}
